import { Close as CloseIcon } from '@mui/icons-material'
import { Box, IconButton, Paper, Typography, useTheme } from '@mui/material'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import React, { useEffect, useMemo, useState } from 'react'
import DealReferenceContent from './DealReferenceContent'
import OrganizationReferenceContent from './OrganizationReferenceContent'
import ProductReferenceContent from './ProductReferenceContent'
import PromptReferenceContent from './PromptReferenceContent'
import { referenceMenus } from './referenceMenuConfig'

interface ReferencePanelProps {
  menuId: string
  pos: { x: number; y: number }
  size: { w: number; h: number }
  onClose: () => void
  onSelectPrompt?: (prompt: string) => void
  isDocked?: boolean
}

const PANEL_WIDTH = 340
const PANEL_MIN_WIDTH = 260
const PANEL_GAP = 8

const contentRegistry: Record<string, React.FC<any>> = {
  prompts: PromptReferenceContent,
  products: ProductReferenceContent,
  organizations: OrganizationReferenceContent,
  deals: DealReferenceContent,
}

const ReferencePanel: React.FC<ReferencePanelProps> = ({ menuId, pos, size, onClose, onSelectPrompt, isDocked }) => {
  const theme = useTheme()
  const { pinned } = useLayoutContext()
  const [viewport, setViewport] = useState({ w: window.innerWidth, h: window.innerHeight })

  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight })
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const menuConfig = referenceMenus.find(m => m.id === menuId)
  const ContentComponent = contentRegistry[menuId]

  const panelStyle = useMemo(() => {
    const chatCenterX = pos.x + size.w / 2
    const anchorSide = chatCenterX > viewport.w / 2 ? 'left' : 'right'

    let width = PANEL_WIDTH
    let left: number

    if (anchorSide === 'right') {
      const spaceRight = viewport.w - (pos.x + size.w + PANEL_GAP)
      width = Math.min(PANEL_WIDTH, Math.max(PANEL_MIN_WIDTH, spaceRight - 8))
      left = pos.x + size.w + PANEL_GAP
    } else {
      const spaceLeft = pos.x - PANEL_GAP
      width = Math.min(PANEL_WIDTH, Math.max(PANEL_MIN_WIDTH, spaceLeft - 8))
      left = pos.x - width - PANEL_GAP
    }

    left = Math.max(8, Math.min(left, viewport.w - width - 8))

    const maxHeight = viewport.h - pos.y - 16
    const height = Math.min(size.h, maxHeight)

    return { left, top: pos.y, width, height }
  }, [pos.x, pos.y, size.w, size.h, viewport.w, viewport.h])
  if (!ContentComponent) return null

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        top: panelStyle.top,
        left: panelStyle.left,
        width: panelStyle.width,
        height: panelStyle.height,
        zIndex: 1299,
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: theme.palette.background.paper,
        boxShadow: `4px 1px 16px ${theme.palette.background.section}`,
        pt: pinned && isDocked ? 7 : 0,
        transition: 'padding 0.3s ease',
      }}
    >
      <Box
        sx={{
          height: 36,
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}
      >
        {menuConfig && (
          <>
            <menuConfig.icon sx={{ fontSize: 16, color: 'text.ai_agent_text', mr: 0.75 }} />
            <Typography variant="caption" sx={{ fontWeight: 600, flex: 1, fontSize: '0.75rem' }}>
              {menuConfig.label}
            </Typography>
          </>
        )}
        <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary', p: 0.25 }}>
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <ContentComponent onSelectPrompt={onSelectPrompt} />
      </Box>
    </Paper>
  )
}

export default ReferencePanel
